import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import {
  DesktopChangePasswordPage,
  DesktopHomePage,
  DesktopModulePage,
} from './desktop/DesktopRoutePages';
import { DesktopShell } from './desktop/DesktopShell';
import { ACCOUNT_PASSWORD_PATH, DESKTOP_MODULE_ROUTES } from './desktop/routes';
import { LoginPage } from './lib/auth/LoginPage';
import { useSession } from './lib/auth/session';
import type { UsePermissionsOptions } from './lib/permissions/usePermissions';
import { SCANNER_MODES } from './scanner/modes';
import { ScannerMenu } from './scanner/ScannerMenu';
import { ScannerPasswordScreen } from './scanner/ScannerPasswordScreen';
import { ScannerModuleScreen, ScannerShell } from './scanner/ScannerShell';
import { SCANNER_PASSWORD_SEGMENT, SCANNER_PATH } from './scanner/routes';
import { TagScanScreen } from './scanner/TagScanScreen';

export interface AppProps {
  /**
   * inject ได้เพื่อการทดสอบเท่านั้น — production render จาก `main.tsx` แบบไม่ส่งค่า
   * แล้วปล่อยให้ shell อ่าน `VITE_API_BASE_URL` เอง
   */
  readonly permissionsOptions?: UsePermissionsOptions;
}

/**
 * Route แยกสองโครงตาม D-037:
 * - `/`        Desktop ERP shell (ความหนาแน่นสูง, เมาส์/คีย์บอร์ด)
 * - `/scanner` Android handheld scanner-first shell (ปุ่มใหญ่, สถานะ offline)
 *
 * Desktop เป็น layout route: `DesktopShell` โหลดสิทธิ์ครั้งเดียวแล้วส่งลงให้ทุก
 * หน้าจอลูก ส่วน route ของโมดูลถูก generate จาก `DESKTOP_MODULE_ROUTES` เพื่อให้
 * ทุกรายการเมนูที่มี `moduleCode` มี guard คู่กันเสมอ — การซ่อนลิงก์แล้วปล่อย URL
 * เปิดทิ้งไว้คือช่องโหว่ที่โครงนี้ตั้งใจปิด
 *
 * `/scanner` อยู่นอก Desktop shell โดยเจตนา: เป็นคนละ shell และ M11 กำหนดให้
 * ทำงานแบบ offline ได้ การผูกมันไว้กับผลของ `GET /v1/me/permissions` จะทำให้
 * เครื่องสแกนเปิดไม่ได้ทันทีที่ WAN หลุด — ด่านสิทธิ์จริงของทุก scan อยู่ที่ backend
 *
 * **ด่านแรกของทั้งสอง shell คือ token ไม่ใช่ permission set**: ยังไม่มี token =
 * เห็นหน้าเข้าสู่ระบบเท่านั้น ไม่มีเมนู ไม่มีหน้าจอธุรกิจ และไม่มีคำขอออกไปหา API
 * ธุรกิจแม้แต่คำขอเดียว การตรวจ token เป็นการอ่านค่าในเครื่องล้วน ๆ เครื่องสแกนจึง
 * ยังเปิดได้ตอน WAN หลุดตามเดิม ต่างจากการเรียก endpoint สิทธิ์
 *
 * `device_kind` ที่ส่งไปกับ login มาจาก shell ที่ผู้ใช้อยู่ ไม่ใช่จากตัวเลือกบนหน้าจอ
 * — contract ระบุว่ามันเลือกได้แค่อายุ session ไม่ใช่สิทธิ์ (D-046)
 */
export function App({ permissionsOptions }: AppProps = {}) {
  const { token } = useSession();
  const signedIn = token !== null;

  return (
    <Routes>
      {/*
        shell เครื่องสแกนเป็น layout route ด้วยเหตุผลเดียวกับ Desktop: เมื่อยังไม่มี
        token element ที่เรนเดอร์คือหน้าเข้าสู่ระบบซึ่ง**ไม่มี `<Outlet />`** หน้าจอ
        ลูกทุกตัวจึงไปไม่ถึง ด่านเดียว กลไกเดียว ทั้งสอง shell

        ใต้ด่าน token ลงมา `ScannerShell` เป็น layout route ชั้นที่สอง (D-109):
        `index` คือเมนูหลัก (`ScannerMenu`) ส่วน route ของแต่ละโมดูล generate จาก
        `SCANNER_MODES` เหมือนที่ `DESKTOP_MODULE_ROUTES` generate route ของ
        Desktop ด้านล่าง — เพิ่มโมดูลที่สี่คือแก้ข้อมูลที่ `modes.ts` ที่เดียว
        ไม่ใช่มาเติม `<Route>` ที่นี่
      */}
      <Route
        path={SCANNER_PATH}
        element={signedIn ? <Outlet /> : <LoginPage deviceKind="HANDHELD" />}
      >
        <Route element={<ScannerShell />}>
          <Route index element={<ScannerMenu />} />
          {/*
            `TAG_SCAN` เป็นการอ่านออนไลน์ ไม่ใช่คำสั่งที่เข้าคิวออฟไลน์เหมือนสามโหมด
            เดิม จึงเรนเดอร์ `TagScanScreen` ตรงที่นี่ ไม่ผ่าน `ScannerModuleScreen`
            ที่รู้จักเฉพาะสามโหมดคิวออฟไลน์ (D-170)
          */}
          {SCANNER_MODES.map((mode) =>
            mode.id === 'TAG_SCAN' ? (
              <Route key={mode.id} path={mode.path} element={<TagScanScreen />} />
            ) : (
              <Route key={mode.id} path={mode.path} element={<ScannerModuleScreen mode={mode.id} />} />
            ),
          )}
        </Route>
        <Route path={SCANNER_PASSWORD_SEGMENT} element={<ScannerPasswordScreen />} />
      </Route>
      <Route
        element={
          signedIn ? (
            <DesktopShell permissionsOptions={permissionsOptions} />
          ) : (
            // layout route ที่ไม่มี `<Outlet />` — route ลูกทุกตัวจึงไม่ถูกเรนเดอร์
            // ผู้ที่พิมพ์ URL ของหน้าจอธุรกิจตรง ๆ ตอนยังไม่ได้เข้าสู่ระบบ จะเจอ
            // หน้าเข้าสู่ระบบ ไม่ใช่โครงหน้าจอนั้นที่ว่างเปล่า
            <LoginPage deviceKind="DESKTOP" />
          )
        }
      >
        <Route path="/" element={<DesktopHomePage />} />
        {/*
          เปลี่ยนรหัสผ่านของตัวเองอยู่ใต้ layout route เดียวกับหน้าจอธุรกิจ จึงถูก
          ปฏิเสธด้วย**กลไกเดียวกัน**เมื่อยังไม่มี token — แต่ไม่ได้ถูก generate จาก
          `DESKTOP_MODULE_ROUTES` เพราะไม่มีโมดูล/สิทธิ์ผูกกับมัน:

          ทุกคนที่เข้าสู่ระบบได้ย่อมมีรหัสผ่านของตัวเองให้เปลี่ยน RBAC matrix จึงไม่มี
          module code สำหรับเรื่องนี้ และ**ห้ามประดิษฐ์ขึ้นมาใหม่** ผู้ใช้ที่ `modules`
          ว่างเปล่าต้องยังเปลี่ยนรหัสผ่านของตัวเองได้ ไม่งั้นคนที่เพิ่งถูกผู้ดูแลระบบ
          รีเซ็ตรหัสให้แต่ยังไม่ได้รับสิทธิ์โมดูลใดจะติดอยู่กลางทาง
        */}
        <Route path={ACCOUNT_PASSWORD_PATH} element={<DesktopChangePasswordPage />} />
        {DESKTOP_MODULE_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={<DesktopModulePage route={route} />} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
